import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import {
  AvatarDropdown,
  AvatarName,
  ErrorNotice,
  Footer,
  TaskCenter,
} from '@/components';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message } from 'antd';
import defaultSettings from '../config/defaultSettings';
import { commonAPI } from './constant/auth';
import { errorConfig } from './requestErrorConfig';
import {
  extractCurrentUserInfo,
  extractCurrentUserPermissions,
  fetchAvailableApps,
  fetchCurrentUserInfo,
  fetchUserPermissions,
  isUserDisabled,
  normalizePermissionCodes,
} from './services/sd/user';
import { userCache } from './store/userInfo';
import { clearActiveApiKey } from './utils/apiKey';
import { sanitizeRememberedAppScopes } from './utils/appScope';
import { auth } from './utils/auth';
import { loadCryptoRegistry } from './utils/fileCrypto';
import { logoutToLogin } from './utils/session';
import { encryptSM4_CBC, getTransmissionSm4KeyId } from './utils/sm4';

const isDev = process.env.NODE_ENV === 'development';
const debug = true;
const API_BASE_URL = (process.env.API_BASE_URL as string) || '';
const SUPER_ONLY_PERMISSION = '__super_only__';
let lastPermissionRefreshKey = '';
let permissionRefreshVersion = 0;
let currentUserCheckVersion = 0;
let lastUserCheckKey = '';
let accessContextRefreshInFlight: Promise<void> | null = null;
let lastAccessContextRefreshAt = 0;
let forceAccessContextRefresh = false;
const ACCESS_CONTEXT_REFRESH_INTERVAL_MS = 10 * 1000;
const routePermissions: Array<{
  prefix: string;
  permissions?: string[];
  superOnly?: boolean;
}> = [
  {
    prefix: '/overview',
    permissions: ['dashboard.view', 'dashboard.manage'],
  },
  { prefix: '/application', superOnly: true },
  {
    prefix: '/space',
    permissions: ['storage.view', 'storage.manage'],
  },
  // 目录管理页：读 / 写 / 管理三级入口都允许进入，由页面内动作权限再细分。
  {
    prefix: '/catalogue',
    permissions: ['catalog.view', 'catalog.manage'],
  },
  { prefix: '/global_search', permissions: ['search.view', 'search.manage'] },
  { prefix: '/tag', permissions: ['tag.view', 'tag.manage'] },
  { prefix: '/metadata', permissions: ['metadata.view', 'metadata.manage'] },
  { prefix: '/model', permissions: ['model.view', 'model.manage'] },
  { prefix: '/user', permissions: ['user.view', 'user.manage'] },
  { prefix: '/auth', permissions: ['role.view', 'role.manage'] },
  { prefix: '/token', permissions: ['api_key.view', 'api_key.manage'] },
  {
    prefix: '/crypto-key',
    permissions: ['storage.crypto_key.view', 'storage.crypto_key.manage'],
  },
  { prefix: '/log', permissions: ['log.view', 'log.manage'] },
  { prefix: '/dictionary', permissions: ['dict.view', 'dict.manage'] },
  { prefix: '/domain', superOnly: true },
];

const getPermissionsForPath = (path?: string) => {
  if (!path) return undefined;
  if (path.startsWith('/log/login')) {
    return undefined;
  }
  const matched = routePermissions.find((item) => path.startsWith(item.prefix));
  if (!matched) return undefined;
  if (matched.superOnly) return [SUPER_ONLY_PERMISSION];
  return matched.permissions;
};

const canAccessPath = (
  path: string | undefined,
  permissions: string[],
  isSuperAdmin: boolean,
) => {
  if (isSuperAdmin) return true;
  const requiredPermissions = getPermissionsForPath(path);
  if (!requiredPermissions) return true;
  if (requiredPermissions.includes(SUPER_ONLY_PERMISSION)) return false;
  return requiredPermissions.some((permission) =>
    permissions.includes(permission),
  );
};

const isAbsoluteUrl = (url: string) =>
  /^https?:\/\//i.test(url) || url.startsWith('//');

const resolveRequestUrl = (url: string) => {
  if (!url || isAbsoluteUrl(url)) return url;
  if (!url.startsWith('/')) return url;
  if (
    url.startsWith('/api') ||
    url.startsWith('/apiv2') ||
    url.startsWith('/s3-msec')
  ) {
    return API_BASE_URL ? `${API_BASE_URL}${url}` : url;
  }
  return url;
};

const isPermissionDeniedResponse = (code: unknown, msg: unknown) => {
  const codeNum = Number(code);
  if (codeNum === 403) return true;
  const text = String(msg || '').toLowerCase();
  return (
    text.includes('无权限') ||
    text.includes('没有权限') ||
    text.includes('forbidden') ||
    text.includes('permission denied')
  );
};

type RuntimeInitialState = {
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  permissionRefreshKey?: number;
};

const notifyPermissionChange = (
  setInitialState: (updater: (state: any) => any) => void,
) => {
  window.dispatchEvent(new Event('user-cache-update'));
  setInitialState((state: RuntimeInitialState | undefined) => ({
    ...state,
    permissionRefreshKey: (state?.permissionRefreshKey || 0) + 1,
  }));
};

const redirectToOverviewIfForbidden = (
  path: string | undefined,
  canAccessCurrentPath: boolean,
) => {
  if (history.location.pathname !== path) {
    return;
  }
  if (path !== '/overview' && !canAccessCurrentPath) {
    history.replace({
      pathname: '/overview',
    });
  }
};

const resetRouteGuards = () => {
  lastPermissionRefreshKey = '';
  permissionRefreshVersion += 1;
  currentUserCheckVersion += 1;
  lastUserCheckKey = '';
};

const refreshUserAccessContext = async (
  setInitialState: (updater: (state: any) => any) => void,
  force = false,
) => {
  if (userCache.isSuperAdmin === 1 || !userCache.token || !userCache.guid) {
    return;
  }
  const now = Date.now();
  if (
    !force &&
    now - lastAccessContextRefreshAt < ACCESS_CONTEXT_REFRESH_INTERVAL_MS
  ) {
    return;
  }
  if (accessContextRefreshInFlight) {
    await accessContextRefreshInFlight;
    return;
  }

  accessContextRefreshInFlight = (async () => {
    try {
      const apps = await fetchAvailableApps(userCache.guid || undefined);
      const appList = Array.isArray(apps) ? apps : [];
      userCache.apps = appList;

      const prevAppUuid = userCache.currentAppUuid || '';
      const nextAppUuid = appList.some((app: any) => app.uuid === prevAppUuid)
        ? prevAppUuid
        : appList.find((app: any) => app.isDefault === 1)?.uuid ||
          appList[0]?.uuid ||
          '';
      userCache.currentAppUuid = nextAppUuid;
      sanitizeRememberedAppScopes(
        appList.map((app: any) => String(app.uuid || '')),
        nextAppUuid,
      );

      if (prevAppUuid !== nextAppUuid) {
        clearActiveApiKey();
        lastPermissionRefreshKey = '';
      }

      if (!nextAppUuid) {
        userCache.permissions = [];
        userCache.permissionAppUuid = '';
      } else if (
        force ||
        userCache.permissionAppUuid !== nextAppUuid ||
        prevAppUuid !== nextAppUuid
      ) {
        try {
          const permRes = await fetchUserPermissions(nextAppUuid);
          userCache.permissions = normalizePermissionCodes(
            permRes,
            nextAppUuid,
          );
          userCache.permissionAppUuid = nextAppUuid;
        } catch {
          // 保留上一次可用的权限缓存，避免权限接口瞬时失败时把页面误判成无权限。
        }
      }
      notifyPermissionChange(setInitialState);
    } catch {
      // ignore
    } finally {
      lastAccessContextRefreshAt = Date.now();
      forceAccessContextRefresh = false;
      accessContextRefreshInFlight = null;
    }
  })();

  await accessContextRefreshInFlight;
};

const validateCurrentUserForPath = async (
  path: string | undefined,
  setInitialState: (updater: (state: any) => any) => void,
) => {
  if (!path || path === '/login' || (!userCache.token && !userCache.guid)) {
    return true;
  }

  const userCheckKey = [
    userCache.token,
    userCache.guid,
    userCache.currentAppUuid,
    path,
  ].join(':');

  if (userCheckKey === lastUserCheckKey) {
    return true;
  }

  const currentVersion = ++currentUserCheckVersion;

  try {
    const userInfoRes = await fetchCurrentUserInfo();
    if (currentVersion !== currentUserCheckVersion) {
      return true;
    }

    if (isUserDisabled(userInfoRes)) {
      message.error('当前用户已被禁用，请重新登录');
      resetRouteGuards();
      logoutToLogin({
        setInitialState,
        preserveRedirect: false,
      });
      return false;
    }

    const currentUserInfo = extractCurrentUserInfo(userInfoRes);
    const nextName =
      currentUserInfo?.realName ||
      currentUserInfo?.name ||
      currentUserInfo?.username;

    if (currentUserInfo?.username !== undefined) {
      userCache.username = currentUserInfo.username || '';
    }
    if (nextName !== undefined) {
      userCache.userName = nextName || '';
    }
    if (currentUserInfo?.avatar !== undefined) {
      userCache.avatar = currentUserInfo.avatar || '';
    }
    const nextPermissions = extractCurrentUserPermissions(userInfoRes);
    if (nextPermissions.length > 0) {
      const prevPermissions = Array.isArray(userCache.permissions)
        ? userCache.permissions
        : [];
      const permissionsChanged =
        prevPermissions.length !== nextPermissions.length ||
        prevPermissions.some(
          (code: string, idx: number) => code !== nextPermissions[idx],
        );
      if (permissionsChanged) {
        userCache.permissions = nextPermissions;
        notifyPermissionChange(setInitialState);
      }
    }

    lastUserCheckKey = userCheckKey;
    return true;
  } catch {
    if (currentVersion !== currentUserCheckVersion) {
      return true;
    }
    return true;
  }
};

const refreshPermissionsForPath = async (
  path: string | undefined,
  setInitialState: (updater: (state: any) => any) => void,
) => {
  if (!path || path === '/login' || userCache.isSuperAdmin === 1) {
    return true;
  }

  const appUuid = userCache.currentAppUuid;
  if (!appUuid) {
    return canAccessPath(path, userCache.permissions || [], false);
  }

  const refreshKey = `${userCache.token}:${appUuid}:${path}`;
  if (refreshKey === lastPermissionRefreshKey) {
    return canAccessPath(path, userCache.permissions || [], false);
  }

  lastPermissionRefreshKey = refreshKey;
  const currentVersion = ++permissionRefreshVersion;

  try {
    const permRes = await fetchUserPermissions(appUuid);
    if (currentVersion !== permissionRefreshVersion) {
      return true;
    }

    const permissions = normalizePermissionCodes(permRes, appUuid);
    userCache.permissions = permissions;
    userCache.permissionAppUuid = appUuid;
    notifyPermissionChange(setInitialState);
    return canAccessPath(path, permissions, false);
  } catch {
    if (currentVersion !== permissionRefreshVersion) {
      return true;
    }

    return canAccessPath(path, userCache.permissions || [], false);
  }
};
/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
// 关闭登录拦截，直接提供默认用户，方便无鉴权访问
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  permissionRefreshKey?: number;
}> {
  return {
    currentUser: {
      name: 'guest',
      access: 'admin',
    } as API.CurrentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
    permissionRefreshKey: 0,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const watermarkContent =
    userCache.userName || initialState?.currentUser?.name || '';

  return {
    actionsRender: () => [
      <ErrorNotice key="notice" />,
      <TaskCenter key="task" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: watermarkContent,
      gap: [80, 80],
    },
    onPageChange() {
      const { location } = history;
      // 未登录且非登录页，跳转登录
      // const access_token = localStorage.getItem('access_token')
      // console.log('onPageChange', access_token)
      // if (!access_token && location.pathname !== '/login') {
      //   history.replace({
      //     pathname: '/login'
      //   })
      // }
      // 未登录状态，跳转登录页面
      if (
        !userCache.token &&
        !userCache.guid &&
        location.pathname !== '/login'
      ) {
        history.replace({
          pathname: '/login',
        });
      }
      void (async () => {
        if (userCache.token) {
          void loadCryptoRegistry().catch(() => {});
        }
        await refreshUserAccessContext(
          setInitialState,
          forceAccessContextRefresh,
        );
        if (!userCache.isSuperAdmin && !userCache.currentAppUuid) {
          const apps = Array.isArray(userCache.apps) ? userCache.apps : [];
          if (apps.length > 0) {
            userCache.currentAppUuid = apps[0]?.uuid || '';
            if (userCache.currentAppUuid) {
              window.location.reload();
              return;
            }
          }
        }
        const isCurrentUserValid = await validateCurrentUserForPath(
          location.pathname,
          setInitialState,
        );
        if (!isCurrentUserValid) {
          return;
        }
        const canAccessCurrentPath = await refreshPermissionsForPath(
          location.pathname,
          setInitialState,
        );
        redirectToOverviewIfForbidden(location.pathname, canAccessCurrentPath);
      })();
    },
    menuDataRender: (menuData) => {
      const isSuperAdmin = userCache.isSuperAdmin === 1;
      const permissions = new Set<string>(userCache.permissions || []);
      const allowItem = (item: any) => {
        const requiredPermissions = getPermissionsForPath(item.path);
        if (!requiredPermissions) return true;
        if (isSuperAdmin) return true;
        if (requiredPermissions.includes(SUPER_ONLY_PERMISSION)) return false;
        return requiredPermissions.some((permission) =>
          permissions.has(permission),
        );
      };
      const filterMenu = (items: any[]): any[] =>
        items
          .filter((item) => allowItem(item))
          .map((item) => ({
            ...item,
            children: item.children ? filterMenu(item.children) : item.children,
          }));
      return filterMenu(menuData as any[]);
    },
    footerRender: () => <Footer />,
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  timeout: 60000,
  // 请求拦截器
  requestInterceptors: [
    async (config: any) => {
      const originalUrl = String(config.url || '');
      config.url = resolveRequestUrl(originalUrl);
      let { data } = config;
      if (!data) {
        data = { empty: 'empty' };
        config.data = data;
      }
      if (
        !commonAPI.includes(originalUrl) &&
        !data.appUuid &&
        userCache.currentAppUuid
      ) {
        data.appUuid = userCache.currentAppUuid;
      }
      const params = encryptSM4_CBC(JSON.stringify(data));
      const headers = await auth(originalUrl, params);
      if (headers) {
        Object.assign(config.headers, headers);
      }
      config.headers['x-crypto-key-id'] = getTransmissionSm4KeyId();
      const debug_data = debug ? data : {};
      config.data = JSON.stringify({
        params,
        debug_data,
      });
      return config;
    },
  ],
  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as any;
      const { code, msg } = data;
      const skipErrorHandler = Boolean(
        (response as any)?.config?.skipErrorHandler,
      );
      console.log('response', response);
      // const { message } = App.useApp()
      if (code === 401) {
        resetRouteGuards();
        logoutToLogin({ preserveRedirect: true });
        return Promise.reject(data);
      } else if (isPermissionDeniedResponse(code, msg)) {
        resetRouteGuards();
        forceAccessContextRefresh = true;
        if (userCache.isSuperAdmin !== 1) {
          userCache.permissions = [];
          userCache.permissionAppUuid = '';
          window.dispatchEvent(new Event('user-cache-update'));
        }
        if (history.location.pathname !== '/overview') {
          history.replace({
            pathname: '/overview',
          });
        }
        if (!skipErrorHandler) {
          message.error(msg || '当前账号权限已变更，请重新选择可访问应用');
        }
        return Promise.reject(data);
      } else if (code !== 0) {
        if (!skipErrorHandler) {
          message.error(msg);
        }
        return Promise.reject(data);
      }
      return response;
    },
  ],
};
